import { SelectField } from '@lovoldsystem/ui'
import { AddCircleOutline, DeleteOutlineOutlined } from '@mui/icons-material'
import { Box, styled, useTheme } from '@mui/material'
import {
  GridColumns,
  GridEventListener,
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useGetProducts } from 'src/api/product'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { ProductWithSupplierOption } from 'src/components/common/ProductOption'
import BasicDatePicker from 'src/components/DateTimePicker'
import { IProduct } from 'src/generated-types'
import { IPositionDrawingProduct } from 'src/generated-types/position-drawing-product'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { /*getFormattedDate,*/ getThemeColor } from 'src/utils'
import { generateUUID } from 'src/utils/function'
import GenericTable from '../../../components/table/GenericTable'
import { APIResultCount } from 'src/constant'

export const CustomTemplateProductsGrid = styled(GenericTable)(({ theme }) => ({
  '& .MuiDataGrid-row.MuiDataGrid-row--lastVisible.MuiDataGrid-row': {
    border: `1px dashed ${getThemeColor(theme, MUIThemeColors.primaryMain)}`,
    borderRadius: '4px',
    overflow: 'hidden',
  },
}))

const MIN_SEARCH_LENGTH = 3
interface ProductEditInputCellProps extends GridRenderCellParams {
  onProductChange: (rowId: EntityId, product: IProduct, api: any, field: string, event: any) => void
}

const ProductEditInputCell: React.FC<ProductEditInputCellProps> = ({ id, api, field, onProductChange }) => {
  const { t } = useTranslation(['common'])
  const row = api.getRow(id)
  const [searchInput, setSearchInput] = useState('')
  const debounceRef = useRef<number>()

  const shouldSearch = searchInput.length >= MIN_SEARCH_LENGTH
  const { data: productsData, isFetching } = useGetProducts(
    shouldSearch ? searchInput : undefined,
    0,
    APIResultCount.Max,
    { enabled: shouldSearch },
  )
  const products = useMemo(() => (shouldSearch ? productsData?.items ?? [] : []), [productsData, shouldSearch])

  const noOptionsText = !shouldSearch
    ? t('typeAtLeast3Characters', { ns: 'common' })
    : t('noResultFound', { ns: 'common' })

  const handleInputChange = useCallback((_event: any, value: string, reason: string) => {
    if (reason === 'input' || reason === 'clear') {
      clearTimeout(debounceRef.current)
      debounceRef.current = window.setTimeout(() => setSearchInput(value), 300)
    }
  }, [])

  const handleChange = useCallback(
    (event, product) => {
      if (!product) return
      onProductChange(row.id, product, api, field, event)
    },
    [row?.id, api, field, onProductChange],
  )

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const input = node.querySelector('input')
      if (input) setTimeout(() => input.focus(), 0)
    }
  }, [])

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      <SelectField
        sx={{ width: '100%', minWidth: window.innerWidth < 600 ? '100vw' : '42vw', backgroundColor: 'white' }}
        label={t('search', { ns: 'common' })}
        data={products}
        getOptionLabel={(o: IProduct | string) => (typeof o === 'string' ? o : `${o.number} - ${o.description}`)}
        onChange={handleChange}
        onInputChange={handleInputChange}
        loading={isFetching}
        filterOptions={(x: IProduct[]) => x}
        noOptionsText={noOptionsText}
        openOnFocus
        ListboxProps={{
          style: { maxWidth: 'none' },
        }}
        renderOption={(props: any, o: IProduct) => (
          <ProductWithSupplierOption
            liProps={props}
            option={o}
            minWidth={window.innerWidth < 600 ? '98vw' : '40vw'}
          />
        )}
        isOptionEqualToValue={(o: IProduct, v: IProduct) => o.id === v.id}
      />
    </Box>
  )
}

interface ITemplateProductsGridProps {
  onCellEditCommit?: GridEventListener<'cellEditCommit'> | undefined
  setPositionDrawingProducts: React.Dispatch<React.SetStateAction<IPositionDrawingProduct[]>>
  positionDrawingProducts: IPositionDrawingProduct[]
}

export const TemplateProductsGrid: React.FC<ITemplateProductsGridProps> = ({
  onCellEditCommit,
  setPositionDrawingProducts,
  positionDrawingProducts,
}) => {
  const { t } = useTranslation(['mooring', 'common', 'component', 'supplier', 'product', 'admin-position-drawing'])
  const theme = useTheme()
  const apiRef = useGridApiRef()

  const onProductChange = useCallback(
    (rowId: EntityId, product: IProduct, api: any, field: string, event: any) => {
      setPositionDrawingProducts(prev =>
        prev.map(p => {
          if (p.id === rowId) {
            return {
              ...p,
              productNumber: product.number,
              productId: product.id,
              quantity: p.quantity || 1,
              product: product,
            }
          }
          return p
        }),
      )
      api.setEditCellValue({ id: rowId, field, value: product.number }, event)
    },
    [setPositionDrawingProducts],
  )

  const renderProductEditInputCell = useCallback(
    (params: GridRenderCellParams) => {
      return <ProductEditInputCell {...params} onProductChange={onProductChange} />
    },
    [onProductChange],
  )

  const handleClick = () => {
    const id = generateUUID()
    setPositionDrawingProducts(prev => [...prev, { id } as unknown as IPositionDrawingProduct])
    apiRef.current.updateRows([{ id, isNew: true }])
    apiRef.current.setRowMode(id, 'edit')
    setTimeout(() => {
      apiRef.current.scrollToIndexes({
        rowIndex: apiRef.current.getRowsCount() - 1,
      })
      apiRef.current.setCellFocus(id, 'productNumber')
    }, 150)
  }

  const renderDateEditInput = (params: GridRenderEditCellParams) => {
    const { id, api, field, value } = params

    const handleChange = date => {
      api.setEditCellValue({
        id: id,
        field: field,
        value: date,
      })
    }
    return <BasicDatePicker isWithoutAppForm name="dueDate" onChange={date => handleChange(date)} value={value} />
  }

  const handleDeleteClick = (id: EntityId) => {
    const newProductList = positionDrawingProducts.filter(p => p.id != id)
    setPositionDrawingProducts(newProductList)
    toast.success(t('product-delete-from-current-template', { ns: 'mooring' }))
  }

  const columns: GridColumns = [
    { field: 'productId', headerName: 'ProductId', type: 'number', hide: true },
    {
      field: 'positionDrawingNumber',
      headerName: '#',
      flex: 0.3,
      editable: true,
      sortable: false,
      renderCell: (params: any) => {
        return params.row.positionDrawingNumber
      },
    },
    {
      field: 'productNumber',
      headerName: t('productNumber', { ns: 'component' }),
      flex: 0.8,
      minWidth: 150,
      editable: true,
      renderEditCell: renderProductEditInputCell,
      valueGetter: (params: any) => {
        return params.row.productNumber
      },
    },
    {
      field: 'supplierName',
      headerName: t('supplier', { ns: 'supplier' }),
      flex: 1,
      minWidth: 130,
      valueGetter: (params: any) => {
        return params.row.product?.supplier?.name
      },
    },
    {
      field: 'description',
      headerName: t('productDescription', { ns: 'component' }),
      flex: 2.5,
      minWidth: 170,
      renderCell: params => {
        return params.row.product?.description
      },
    },
    {
      field: 'quantity',
      headerName: t('quantity', { ns: 'component' }),
      editable: true,
      flex: 0.5,
      minWidth: 70,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'action',
      headerName: '',
      type: 'actions',
      align: 'center',
      width: 100,
      renderCell: ({ row }) => {
        return (
          <Flex.Row alignItems={'center'} gap={1} marginBottom={'2px'}>
            <GridActionButton
              buttonIcon={<DeleteOutlineOutlined />}
              toolTipTitle={t('delete', { ns: 'common' })}
              shadowWeight={0}
              variant="outlined"
              width="32px"
              height="32px"
              onClick={() => {
                handleDeleteClick(row.id)
              }}
            />
          </Flex.Row>
        )
      },
    },
    /*{
      field: 'unit',
      headerName: t('unit', { ns: 'component' }),
      flex: 1,
      minWidth: 70,
      valueGetter: (params: any) => {
        return params.row.product?.unit?.name
      },
    },
    { field: 'batchNo', headerName: t('batchNumber', { ns: 'component' }), flex: 1, editable: true, minWidth: 100 },
    {
      field: 'installationDate',
      headerName: t('installationDate'),
      flex: 1,
      minWidth: 170,
      type: 'date',
      editable: true,
      renderCell: ({ value }) => getFormattedDate(value),
      renderEditCell: renderDateEditInput,
    },*/
  ]

  return (
    <>
      <CustomTemplateProductsGrid
        id="templateProducts"
        data={positionDrawingProducts}
        apiRef={apiRef}
        enableToolbar={true}
        toolbarOptions={[
          {
            label: t(''),
            Component: (
              <Box marginTop={'-70px'}>
                <GridActionButton
                  onClick={handleClick}
                  toolTipTitle={''}
                  buttonText={t('addProduct', { ns: 'product' })}
                  bgColor={theme.palette.white.main}
                  height="38px"
                  width="142px"
                  buttonIcon={
                    <AddCircleOutline
                      style={{ fontSize: '16px' }}
                      htmlColor={theme.palette.primaryMain[theme.palette.mode]}
                    />
                  }
                />
              </Box>
            ),
          },
        ]}
        columns={columns}
        noRowsMessage={t('selectedTemplateHasNoProducts', { ns: 'mooring' })}
        onCellEditCommit={onCellEditCommit}
        onCellClick={(params) => {
          if (params.isEditable) {
            const editRows = apiRef.current.getEditRowsModel()
            Object.keys(editRows).forEach(rowId => {
              Object.keys(editRows[rowId]).forEach(field => {
                if (rowId !== String(params.id) || field !== params.field) {
                  apiRef.current.commitCellChange({ id: rowId, field })
                  apiRef.current.setCellMode(rowId, field, 'view')
                }
              })
            })
            apiRef.current.setCellMode(params.id, params.field, 'edit')
          }
        }}
        height={'calc(100vh - 714px)'}
        autoHeight={false}
      />
    </>
  )
}

